import { Box, Typography, Card, Skeleton } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import FavoriteIcon from '@mui/icons-material/Favorite'
import { getProxiedImageUrl, FALLBACK_POSTER_URL } from '@aperture/ui'

interface RatingItem {
  id: string
  type: 'movie' | 'series'
  title: string
  year: number | null
  posterUrl: string | null
  rating: number
  ratedAt: Date
}

interface RecentRatingsListProps {
  ratings: RatingItem[]
  loading?: boolean
}

function formatRatedDate(date: Date, locale: string): string {
  const d = new Date(date)
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function HeartDisplay({ rating }: { rating: number }) {
  // Show filled hearts for the rating (max 10)
  const filledHearts = Math.min(rating, 10)
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {Array.from({ length: filledHearts }).map((_, i) => (
        <FavoriteIcon
          key={i}
          sx={{
            fontSize: 14,
            color: '#ec4899',
          }}
        />
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
        {rating}/10
      </Typography>
    </Box>
  )
}

export function RecentRatingsList({ ratings, loading }: RecentRatingsListProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  if (loading) {
    return (
      <Card
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={2}>
          {t('dashboard.recentRatings')}
        </Typography>
        {Array.from({ length: 3 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <Skeleton variant="rounded" width={48} height={72} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" />
              <Skeleton width="40%" />
              <Skeleton width="30%" />
            </Box>
          </Box>
        ))}
      </Card>
    )
  }

  if (ratings.length === 0) {
    return (
      <Card
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={2}>
          {t('dashboard.recentRatings')}
        </Typography>
        <Box
          sx={{
            py: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <FavoriteIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.noRatingsYet')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('dashboard.noRatingsHint')}
          </Typography>
        </Box>
      </Card>
    )
  }

  return (
    <Card
      sx={{
        p: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={2}>
        {t('dashboard.recentRatings')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {ratings.map((item) => (
          <Box
            key={item.id}
            onClick={() => navigate(`/${item.type === 'movie' ? 'movies' : 'series'}/${item.id}`)}
            sx={{
              display: 'flex',
              gap: 1.5,
              p: 1,
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Box
              component="img"
              src={getProxiedImageUrl(item.posterUrl)}
              alt={item.title}
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.src = FALLBACK_POSTER_URL
              }}
              sx={{
                width: 48,
                height: 72,
                objectFit: 'cover',
                borderRadius: 1,
                flexShrink: 0,
                backgroundColor: 'grey.800',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                noWrap
                title={item.title}
                sx={{ lineHeight: 1.3 }}
              >
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {item.year || t('dashboard.unknownYear')} •{' '}
                {item.type === 'movie' ? t('dashboard.typeMovie') : t('dashboard.typeSeries')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <HeartDisplay rating={item.rating} />
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.ratedOn', { date: formatRatedDate(item.ratedAt, i18n.language) })}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  )
}


